import React from 'react';
import { Cookie, Shield, BarChart3, Megaphone, Settings } from 'lucide-react';

export default function CookiePolicyPage() {
  return (
    <div className="min-h-screen bg-[var(--bg-base)] py-12 px-4">
      <div className="max-w-4xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl font-bold text-stone-900 mb-4 flex items-center gap-3">
            <Cookie className="w-8 h-8 text-violet-600" />
            Cookie Policy
          </h1>
          <p className="text-stone-500">
            Last updated: {new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
          </p>
        </div>

        <div className="prose max-w-none space-y-8">
          <section className="bg-white rounded-xl p-6 border border-stone-200">
            <h2 className="text-xl font-semibold text-stone-900 mb-4">What Are Cookies?</h2>
            <p className="text-stone-600">
              Cookies are small text files that are stored on your device (computer, tablet, or mobile) 
              when you visit a website. They are widely used to make websites work more efficiently and 
              provide information to the website owners.
            </p>
          </section>

          <section className="bg-white rounded-xl p-6 border border-stone-200">
            <h2 className="text-xl font-semibold text-stone-900 mb-4">How We Use Cookies</h2>
            <p className="text-stone-600 mb-4">
              Line Dance Central uses cookies for a variety of purposes. We use them to:
            </p>
            <ul className="list-disc list-inside text-stone-600 space-y-2">
              <li>Keep you signed in to your account</li>
              <li>Remember your preferences and settings</li>
              <li>Understand how you use our website</li>
              <li>Improve our services based on this information</li>
              <li>Provide secure access to booking and payment features</li>
            </ul>
          </section>

          <section className="bg-white rounded-xl p-6 border border-stone-200">
            <h2 className="text-xl font-semibold text-stone-900 mb-4">Types of Cookies We Use</h2>
            
            <div className="space-y-6">
              <div className="border-l-4 border-green-500 pl-4">
                <div className="flex items-center gap-2 mb-2">
                  <Shield className="w-5 h-5 text-emerald-600" />
                  <h3 className="text-lg font-medium text-stone-900">Essential Cookies</h3>
                </div>
                <p className="text-stone-600 mb-2">
                  These cookies are necessary for the website to function properly. They enable core 
                  functionality such as security, network management, and account access. You cannot 
                  opt out of these cookies as the website would not function without them.
                </p>
                <div className="bg-stone-50 rounded p-3 mt-2">
                  <p className="text-sm text-stone-500"><strong>Examples:</strong></p>
                  <ul className="text-sm text-stone-500 mt-1">
                    <li>• Authentication tokens (keeping you logged in)</li>
                    <li>• Session cookies (remembering items in your booking)</li>
                    <li>• Security cookies (protecting against fraud)</li>
                    <li>• Cookie consent preferences</li>
                  </ul>
                </div>
              </div>

              <div className="border-l-4 border-blue-500 pl-4">
                <div className="flex items-center gap-2 mb-2">
                  <BarChart3 className="w-5 h-5 text-blue-600" />
                  <h3 className="text-lg font-medium text-stone-900">Analytics Cookies</h3>
                </div>
                <p className="text-stone-600 mb-2">
                  These cookies help us understand how visitors interact with our website by collecting 
                  and reporting information anonymously. This helps us improve our website and services.
                </p>
                <div className="bg-stone-50 rounded p-3 mt-2">
                  <p className="text-sm text-stone-500"><strong>Information collected:</strong></p>
                  <ul className="text-sm text-stone-500 mt-1">
                    <li>• Pages visited and time spent on each page</li>
                    <li>• How you arrived at our site (search engine, link, etc.)</li>
                    <li>• General location (country/region level only)</li>
                    <li>• Device type and browser used</li>
                  </ul>
                </div>
              </div>

              <div className="border-l-4 border-purple-500 pl-4">
                <div className="flex items-center gap-2 mb-2">
                  <Megaphone className="w-5 h-5 text-purple-600" />
                  <h3 className="text-lg font-medium text-stone-900">Marketing Cookies</h3>
                </div>
                <p className="text-stone-600 mb-2">
                  These cookies may be set through our site by advertising partners. They may be used 
                  to build a profile of your interests and show you relevant advertisements on other sites.
                </p>
                <div className="bg-stone-50 rounded p-3 mt-2">
                  <p className="text-sm text-stone-500"><strong>Purpose:</strong></p>
                  <ul className="text-sm text-stone-500 mt-1">
                    <li>• Displaying relevant advertisements</li>
                    <li>• Limiting how often you see an advert</li>
                    <li>• Measuring the effectiveness of advertising campaigns</li>
                  </ul>
                </div>
              </div>
            </div>
          </section>

          <section className="bg-white rounded-xl p-6 border border-stone-200">
            <h2 className="text-xl font-semibold text-stone-900 mb-4 flex items-center gap-2">
              <Settings className="w-5 h-5 text-violet-600" />
              Managing Your Cookie Preferences
            </h2>
            <p className="text-stone-600 mb-4">
              When you first visit our website, you will be shown a cookie banner that allows you to 
              accept or reject non-essential cookies. You can change your preferences at any time by:
            </p>
            <ul className="list-disc list-inside text-stone-600 space-y-2 mb-4">
              <li>Clicking the "Cookie Settings" link in the website footer</li>
              <li>Clearing your browser cookies and revisiting the site</li>
              <li>Using your browser settings to manage cookies</li>
            </ul>
            <p className="text-stone-600">
              Please note that restricting cookies may impact your experience of the website and limit 
              the services we can offer you.
            </p>
          </section>

          <section className="bg-white rounded-xl p-6 border border-stone-200">
            <h2 className="text-xl font-semibold text-stone-900 mb-4">Third-Party Cookies</h2>
            <p className="text-stone-600 mb-4">
              Some cookies on our site are set by third-party services. These include:
            </p>
            <ul className="list-disc list-inside text-stone-600 space-y-2">
              <li><strong>Square:</strong> For secure payment processing</li>
              <li><strong>Cloudflare:</strong> For security and performance</li>
            </ul>
            <p className="text-stone-600 mt-4">
              We do not control these third-party cookies. Please refer to the respective privacy 
              policies of these third parties for more information about their cookies.
            </p>
          </section>

          <section className="bg-white rounded-xl p-6 border border-stone-200">
            <h2 className="text-xl font-semibold text-stone-900 mb-4">Your Rights</h2>
            <p className="text-stone-600 mb-4">
              Under the UK General Data Protection Regulation (UK GDPR) and the Privacy and Electronic 
              Communications Regulations (PECR), you have the right to:
            </p>
            <ul className="list-disc list-inside text-stone-600 space-y-2">
              <li>Be informed about how we use cookies</li>
              <li>Choose whether to accept non-essential cookies</li>
              <li>Change your cookie preferences at any time</li>
              <li>Request information about the cookies we use</li>
            </ul>
          </section>

          <section className="bg-white rounded-xl p-6 border border-stone-200">
            <h2 className="text-xl font-semibold text-stone-900 mb-4">Contact Us</h2>
            <p className="text-stone-600">
              If you have any questions about our use of cookies, please contact us at:{' '}
              <a href="mailto:hi@linedancecentral.uk" className="text-violet-600 hover:text-violet-700">
                hi@linedancecentral.uk
              </a>
            </p>
          </section>
        </div>
      </div>
    </div>
  );
}
